// src/utils/jjCombatScoring.js

// Score "vierge" pour un combattant Ju-Jitsu Fighting
export function createEmptyJJScore() {
  return {
    // points par partie (techniques + pénalités créditées)
    part1Points: 0,
    part2Points: 0,
    part3Points: 0,

    // nombre d'ippon par partie
    part1Ippon: 0,
    part2Ippon: 0,
    part3Ippon: 0,

    // pénalités
    shido: 0,         // nombre de shido
    chui: 0,          // nombre de chui
    penaltyPoints: 0, // poids total des pénalités (Shido=1, 1er Chui=3)

    // états finaux
    hansokuMake: false,
    forfait: false,
  };
}

// Retourne les points par partie + total
export function getJJPoints(score) {
  const part1 = score.part1Points || 0;
  const part2 = score.part2Points || 0;
  const part3 = score.part3Points || 0;
  const total = part1 + part2 + part3;
  return { part1, part2, part3, total };
}

// Full ippon = au moins un ippon dans chaque partie
export function hasFullIppon(score) {
  return (
    (score.part1Ippon || 0) > 0 &&
    (score.part2Ippon || 0) > 0 &&
    (score.part3Ippon || 0) > 0
  );
}

// Statistiques d'ippon pour les départages
export function getIpponStats(score) {
  const part1 = score.part1Ippon || 0;
  const part2 = score.part2Ippon || 0;
  const part3 = score.part3Ippon || 0;
  const partsWithIppon = (part1 > 0) + (part2 > 0) + (part3 > 0);
  const totalIppon = part1 + part2 + part3;

  return {
    part1,
    part2,
    part3,
    partsWithIppon,
    totalIppon,
  };
}

/**
 * Ajoute une technique dans la partie courante.
 *
 * kind:
 *  - "wazaari" => +1 pt
 *  - "ippon2"  => +2 pts + 1 ippon
 *  - "ippon3"  => +3 pts + 1 ippon
 */
export function addTechnique(score, currentPart, { kind }) {
  const baseKey = `part${currentPart}`;
  const pointsKey = `${baseKey}Points`;
  const ipponKey = `${baseKey}Ippon`;

  const next = { ...score };
  const currentPoints = next[pointsKey] || 0;
  const currentIppon = next[ipponKey] || 0;

  if (kind === "wazaari") {
    next[pointsKey] = currentPoints + 1;
  } else if (kind === "ippon2") {
    next[pointsKey] = currentPoints + 2;
    next[ipponKey] = currentIppon + 1;
  } else if (kind === "ippon3") {
    next[pointsKey] = currentPoints + 3;
    next[ipponKey] = currentIppon + 1;
  }

  return next;
}

/**
 * Retire une technique (correction d'erreur table).
 * Même API que addTechnique, mais en négatif.
 */
export function removeTechnique(score, currentPart, { kind }) {
  const baseKey = `part${currentPart}`;
  const pointsKey = `${baseKey}Points`;
  const ipponKey = `${baseKey}Ippon`;

  const next = { ...score };
  const currentPoints = next[pointsKey] || 0;
  const currentIppon = next[ipponKey] || 0;

  if (kind === "wazaari") {
    next[pointsKey] = Math.max(0, currentPoints - 1);
  } else if (kind === "ippon2") {
    if (currentIppon > 0) {
      next[pointsKey] = Math.max(0, currentPoints - 2);
      next[ipponKey] = currentIppon - 1;
    }
  } else if (kind === "ippon3") {
    if (currentIppon > 0) {
      next[pointsKey] = Math.max(0, currentPoints - 3);
      next[ipponKey] = currentIppon - 1;
    }
  }

  return next;
}

/**
 * Ajoute une pénalité au combattant pénalisé.
 *
 * type: "shido" ou "chui"
 *
 * Règles :
 *  - Shido  => penaltyPoints +1
 *  - 1er Chui => penaltyPoints +3 + pas Hansoku
 *  - 2e Chui  => Hansoku-make (pas de points en plus)
 *  - Cumul penaltyPoints >= 6 => Hansoku-make
 */
export function addPenalty(scorePenalized, { type }) {
  const next = { ...scorePenalized };
  const prevPenaltyPoints = next.penaltyPoints || 0;
  const prevChui = next.chui || 0;

  if (type === "shido") {
    next.shido = (next.shido || 0) + 1;
    next.penaltyPoints = prevPenaltyPoints + 1;
  } else if (type === "chui") {
    const newChui = prevChui + 1;
    next.chui = newChui;

    if (newChui === 1) {
      // 1er Chui : +3 pts de pénalité
      next.penaltyPoints = prevPenaltyPoints + 3;
    } else if (newChui >= 2) {
      // 2e Chui ou plus : Hansoku-make direct
      next.hansokuMake = true;
    }
  }

  // Hansoku-make si cumul >= 6 points de pénalités
  if (!next.hansokuMake && (next.penaltyPoints || 0) >= 6) {
    next.hansokuMake = true;
  }

  return next;
}

/**
 * Retire une pénalité (correction table).
 *
 * type: "shido" ou "chui"
 *
 * - Shido  : shido--, penaltyPoints -1 (min 0)
 * - Chui   :
 *    * si 1 seul Chui => on enlève ce Chui et -3 penaltyPoints
 *    * si >=2 Chui    => on enlève le "dernier" (2e, 3e...) sans toucher aux 3pts du 1er
 *  Puis on recalcule Hansoku-make en fonction de chui & penaltyPoints.
 */
export function removePenalty(scorePenalized, { type }) {
  const next = { ...scorePenalized };

  if (type === "shido") {
    if ((next.shido || 0) > 0) {
      next.shido -= 1;
      next.penaltyPoints = Math.max(0, (next.penaltyPoints || 0) - 1);
    }
  } else if (type === "chui") {
    const chuiCount = next.chui || 0;
    if (chuiCount > 0) {
      if (chuiCount === 1) {
        // on enlève le 1er Chui → on retire les 3 pts associés
        next.chui = 0;
        next.penaltyPoints = Math.max(0, (next.penaltyPoints || 0) - 3);
      } else {
        // on enlève un Chui "supplémentaire" (2e, 3e...),
        // qui n'apporte pas de points de pénalité
        next.chui = chuiCount - 1;
      }
    }
  }

  // Recalcul de Hansoku-make
  next.hansokuMake = false;
  if ((next.chui || 0) >= 2) {
    next.hansokuMake = true;
  } else if ((next.penaltyPoints || 0) >= 6) {
    next.hansokuMake = true;
  }

  return next;
}

export function setForfait(score, value = true) {
  return { ...score, forfait: value };
}

/**
 * Calcul du résultat du combat pour l'enregistrement BD / tableau.
 *
 * Retourne un objet :
 * {
 *   result1, result2,   // "V" / "D" / "N"
 *   points1, points2,   // score "officiel" (50/0 si ippon complet, hansoku, forfait...)
 *   details,            // récap pour audit
 *   needExtraPeriod,    // true si égalité parfaite -> manche supplémentaire
 *   doubleHansoku       // cas rare : hansoku des deux côtés
 * }
 */
export function calculateJJCombatResult(redScore, blueScore) {
  const redPts = getJJPoints(redScore).total;
  const bluePts = getJJPoints(blueScore).total;

  const redFull = hasFullIppon(redScore);
  const blueFull = hasFullIppon(blueScore);

  const redStats = getIpponStats(redScore);
  const blueStats = getIpponStats(blueScore);

  let winner = null; // "red" | "blue"
  let mode = null;   // "forfait" | "hansoku" | "full_ippon" | "points"

  // 1) Forfait / abandon
  if (redScore.forfait && !blueScore.forfait) {
    winner = "blue";
    mode = "forfait";
  } else if (blueScore.forfait && !redScore.forfait) {
    winner = "red";
    mode = "forfait";
  }

  // 2) Hansoku-make
  if (!winner) {
    if (redScore.hansokuMake && !blueScore.hansokuMake) {
      winner = "blue";
      mode = "hansoku";
    } else if (blueScore.hansokuMake && !redScore.hansokuMake) {
      winner = "red";
      mode = "hansoku";
    } else if (redScore.hansokuMake && blueScore.hansokuMake) {
      return {
        result1: "N",
        result2: "N",
        points1: 0,
        points2: 0,
        doubleHansoku: true,
        needExtraPeriod: true,
        details: {
          reason: "double_hansoku_make",
          redScore,
          blueScore,
        },
      };
    }
  }

  // 3) Full ippon
  if (!winner) {
    if (redFull && !blueFull) {
      winner = "red";
      mode = "full_ippon";
    } else if (blueFull && !redFull) {
      winner = "blue";
      mode = "full_ippon";
    }
  }

  // 4) Fin de temps : points + départages
  if (!winner) {
    if (redPts > bluePts) {
      winner = "red";
      mode = "points";
    } else if (bluePts > redPts) {
      winner = "blue";
      mode = "points";
    } else {
      // égalité au nombre de points
      if (redStats.partsWithIppon > blueStats.partsWithIppon) {
        winner = "red";
        mode = "points";
      } else if (blueStats.partsWithIppon > redStats.partsWithIppon) {
        winner = "blue";
        mode = "points";
      } else if (redStats.totalIppon > blueStats.totalIppon) {
        winner = "red";
        mode = "points";
      } else if (blueStats.totalIppon > redStats.totalIppon) {
        winner = "blue";
        mode = "points";
      }
    }
  }

  // 5) Égalité parfaite -> manche supplémentaire
  if (!winner) {
    return {
      result1: "N",
      result2: "N",
      points1: redPts,
      points2: bluePts,
      needExtraPeriod: true,
      details: {
        reason: "perfect_draw",
        redScore,
        blueScore,
        redStats,
        blueStats,
      },
    };
  }

  // 6) Attribution des points "officiels" selon le mode
  let winnerPoints, loserPoints;

  if (mode === "forfait" || mode === "hansoku" || mode === "full_ippon") {
    // Règle : vainqueur = 50, perdant = 0
    winnerPoints = 50;
    loserPoints = 0;
  } else {
    // Victoire "simple" aux points : on garde les scores réels
    winnerPoints = winner === "red" ? redPts : bluePts;
    loserPoints = winner === "red" ? bluePts : redPts;
  }

  const redIsWinner = winner === "red";

  return {
    result1: redIsWinner ? "V" : "D",
    result2: redIsWinner ? "D" : "V",
    points1: redIsWinner ? winnerPoints : loserPoints,
    points2: redIsWinner ? loserPoints : winnerPoints,
    details: {
      reason: mode,
      redScore,
      blueScore,
      redStats,
      blueStats,
    },
  };
}
